import { watchEffect } from "vue";
import { useRoute } from "vue-router";
import { seoConfig } from "@/seo/seoConfig";
import { resolveSiteUrl } from "@/seo/resolveUrl";

const MANAGED_ATTR = "data-seo-managed";

function upsertMetaByAttr(attrName, attrValue, content) {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    el.setAttribute(MANAGED_ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertMetaName(name, content) {
  upsertMetaByAttr("name", name, content);
}

function upsertMetaProperty(property, content) {
  upsertMetaByAttr("property", property, content);
}

// Elimina un meta gestionado por nosotros (data-seo-managed) cuando la ruta
// actual ya no define ese valor, para no arrastrar contenido de la página
// anterior (ej. og:image:alt de un vehículo al navegar a otra ruta).
function removeManagedMeta(attrName, attrValue) {
  const el = document.head.querySelector(`meta[${attrName}="${attrValue}"][${MANAGED_ATTR}="true"]`);
  if (el) el.remove();
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute(MANAGED_ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function formatTitle(title) {
  if (!title) return seoConfig.defaultTitle;
  return title.includes(seoConfig.siteName) ? title : `${title} | ${seoConfig.siteName}`;
}

/**
 * Aplica title, description, canonical, robots, Open Graph y Twitter Card
 * al <head> del documento. No depende de ninguna librería: solo DOM APIs.
 *
 * @param {Object} meta
 * @param {string} [meta.title] - Sin el sufijo de marca; se agrega automáticamente.
 * @param {string} [meta.description]
 * @param {string} [meta.path] - Ruta actual (para construir el canonical por defecto).
 * @param {string} [meta.canonical] - Ruta u URL absoluta; tiene prioridad sobre `path`.
 * @param {string} [meta.robots] - Ej. "index, follow" | "noindex, follow".
 * @param {boolean} [meta.noIndex] - Atajo para robots "noindex, follow".
 * @param {string} [meta.image] - Ruta relativa o URL absoluta de la imagen OG/Twitter.
 * @param {string} [meta.imageAlt]
 * @param {string} [meta.type] - og:type, por defecto "website".
 */
export function applySeoMeta(meta = {}) {
  const {
    title,
    description,
    path,
    canonical,
    robots,
    noIndex,
    image,
    imageAlt,
    type = "website",
  } = meta;

  const finalTitle = formatTitle(title);
  document.title = finalTitle;

  const finalDescription = description || seoConfig.defaultDescription;
  upsertMetaName("description", finalDescription);

  const finalRobots = noIndex ? "noindex, follow" : robots || "index, follow";
  upsertMetaName("robots", finalRobots);

  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
  const canonicalUrl = resolveSiteUrl(canonical || path || currentPath);
  upsertCanonical(canonicalUrl);

  const imageUrl = resolveSiteUrl(image || seoConfig.defaultImagePath);

  upsertMetaProperty("og:site_name", seoConfig.siteName);
  upsertMetaProperty("og:type", type);
  upsertMetaProperty("og:title", finalTitle);
  upsertMetaProperty("og:description", finalDescription);
  upsertMetaProperty("og:url", canonicalUrl);
  upsertMetaProperty("og:image", imageUrl);
  upsertMetaProperty("og:locale", seoConfig.locale);
  if (imageAlt) {
    upsertMetaProperty("og:image:alt", imageAlt);
  } else {
    removeManagedMeta("property", "og:image:alt");
  }

  upsertMetaName("twitter:card", seoConfig.twitterCardType);
  upsertMetaName("twitter:title", finalTitle);
  upsertMetaName("twitter:description", finalDescription);
  upsertMetaName("twitter:image", imageUrl);
  if (seoConfig.twitterSite) upsertMetaName("twitter:site", seoConfig.twitterSite);
}

/**
 * Composable reactivo: vuelve a aplicar la metadata cada vez que cambian
 * las dependencias reactivas usadas dentro de `source`.
 *
 * @param {Object|Function} source - Objeto de metadata, o función que la devuelve
 *                                   (usa refs/computed internamente para reactividad).
 */
export function useSeoMeta(source) {
  const route = useRoute();
  watchEffect(() => {
    const meta = typeof source === "function" ? source() : source;
    if (!meta) return;
    applySeoMeta({ path: route.path, ...meta });
  });
}

export default useSeoMeta;
